/**
 * @file TransportStack.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-12
 * @description The haulage cell: haulier over driver over vehicle, one stack —
 *              they are one decision, who is taking this load. Used by the
 *              line grid's Transport cell (Create / Edit PO) and by Add /
 *              Update Supplied Qty. The planning boards keep their own
 *              columns, one per field, and are not this component.
 *
 *   DRIVER FIRST (Cam, 2026-10-12). While a line has no haulier, no driver
 *   and no vehicle, a click on ANY of the three — or tabbing into the
 *   haulier or vehicle from outside the cell, or Enter / Space / ↓ / typing
 *   on them — opens the Driver menu: a driver brings their haulier and
 *   vehicle (utils/transportAutofill), so asking for the driver first fills
 *   the other two in one choice. The Driver menu offers "No driver" for a
 *   load with none to name; choosing it answers the question and sets
 *   nothing. Once a driver is chosen, or "No driver", each menu opens its
 *   own options as before. A line that already has any of the three works
 *   as it always did.
 *
 *   "No driver" is this component's own state — never a value handed to
 *   `onChange`, so it is never saved, printed or sent. Reopening the screen
 *   asks again, which is harmless: the question is only ever asked of an
 *   empty cell.
 */

import React, { useMemo, useState } from 'react';
import SearchableDropdown from './SearchableDropdown';
import { driverChanges, haulierChanges } from '../utils/transportAutofill';

/** The Driver menu's "none to name" choice. Never leaves this component. */
export const NO_DRIVER = '__no_driver__';
export const NO_DRIVER_LABEL = 'No driver';

const DEFAULT_BLANK = { haulier: 'Haulier', driver: 'Driver', vehicle: 'Vehicle' };

const withCurrent = (options, current) => (current && !options.some(o => o.value === current)
  ? [...options, { value: current, label: current }] : options);

/**
 * @param {string}   haulier, driver, vehicle  the line's current values ('' = none)
 * @param {Function} onChange   (changes) — only the fields the choice changes
 * @param {string[]} hauliers   haulier names
 * @param {Array}    drivers    driver records ({ name, hauliers: [{ name }], default_vehicle_registration })
 * @param {string[]} vehicles   registrations
 * @param {boolean}  disabled
 * @param {string}   title      the stack's title (why it is disabled, say)
 * @param {object}   blank      the blank rows' wording per field
 * @param {object}   menuProps  passed to each SearchableDropdown (compact, escapeOverflow…)
 */
function TransportStack({
  haulier = '', driver = '', vehicle = '', onChange,
  hauliers = [], drivers = [], vehicles = [],
  disabled = false, title, blank = DEFAULT_BLANK, menuProps = {},
}) {
  const labels = { ...DEFAULT_BLANK, ...blank };
  // Has the driver question been answered here — a driver, or "No driver"?
  const [answered, setAnswered] = useState(false);
  const [driverOpen, setDriverOpen] = useState(0);
  const empty = !haulier && !driver && !vehicle;
  const driverFirst = !disabled && !answered && empty;
  const askDriver = () => setDriverOpen(n => n + 1);

  const haulierOptions = useMemo(() => withCurrent(
    [{ value: '', label: labels.haulier }, ...hauliers.map(h => ({ value: h, label: h }))], haulier),
  [hauliers, haulier, labels.haulier]);

  const vehicleOptions = useMemo(() => withCurrent(
    [{ value: '', label: labels.vehicle }, ...vehicles.map(v => ({ value: v, label: v }))], vehicle),
  [vehicles, vehicle, labels.vehicle]);

  // With a haulier chosen, only that haulier's drivers — as on the boards. A
  // driver already on the line stays offered, so nothing is dropped silently.
  const driverOptions = useMemo(() => {
    const pool = haulier ? drivers.filter(d => (d.hauliers || []).some(h => h.name === haulier)) : drivers;
    const names = new Set(pool.map(d => d.name));
    if (driver) names.add(driver);
    return [
      { value: '', label: labels.driver },
      { value: NO_DRIVER, label: NO_DRIVER_LABEL },
      ...[...names].sort().map(name => ({ value: name, label: name })),
    ];
  }, [drivers, haulier, driver, labels.driver]);

  const chooseDriver = (v) => {
    if (v === NO_DRIVER) {
      setAnswered(true);
      // Answers the question; a driver already on the line is taken off.
      if (driver) onChange(driverChanges('', drivers, haulier));
      return;
    }
    if (v) setAnswered(true);
    onChange(driverChanges(v || '', drivers, haulier));
  };

  // Tabbing in from outside onto the haulier or the vehicle asks for the
  // driver; moving about inside the cell does not, so Tab always gets out.
  const onFocus = (e) => {
    if (!driverFirst) return;
    const field = e.target?.closest?.('[data-transport-field]')?.getAttribute('data-transport-field');
    if (field !== 'haulier' && field !== 'vehicle') return;
    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
    askDriver();
  };

  const firstHint = driverFirst ? 'Choose the driver first — or "No driver"' : undefined;

  return (
    <div className="transport-stack" title={title || undefined} onFocus={onFocus}>
      <div data-transport-field="haulier" title={firstHint}>
        <SearchableDropdown
          value={haulier || ''}
          onChange={(v) => onChange(haulierChanges(v || '', drivers))}
          options={haulierOptions}
          placeholder="Haulier"
          searchPlaceholder="Search hauliers..."
          disabled={disabled}
          redirectOpen={driverFirst ? askDriver : null}
          {...menuProps}
        />
      </div>
      <div data-transport-field="driver">
        <SearchableDropdown
          value={driver || (answered ? NO_DRIVER : '')}
          onChange={chooseDriver}
          options={driverOptions}
          placeholder="Driver"
          searchPlaceholder="Search drivers..."
          disabled={disabled}
          openRequest={driverOpen}
          {...menuProps}
        />
      </div>
      <div data-transport-field="vehicle" title={firstHint}>
        <SearchableDropdown
          value={vehicle || ''}
          onChange={(v) => onChange({ vehicle: v || '' })}
          options={vehicleOptions}
          placeholder="Vehicle"
          searchPlaceholder="Search vehicles..."
          disabled={disabled}
          redirectOpen={driverFirst ? askDriver : null}
          {...menuProps}
        />
      </div>
    </div>
  );
}

export default TransportStack;
